/** @jsxImportSource @emotion/react */
import { useContext } from 'react';
import type { ReactElement } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { keyframes } from '@emotion/react';
import { ODSBanner } from 'bc-ods';
import { isThirdPartyProvider } from './RegistrationForm';
import { SmartLoginStateContext } from './context/SmartLoginContextProvider';

const messages = defineMessages({
  signedInAsViaSso: {
    id: 'SSOSignedInBanner.signedInAsViaSso',
    defaultMessage: 'Signed in as {displayName} via SSO',
  },
});

const fadeInAnimation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

function useShouldShowSSOSignedInBanner(): boolean {
  const { provider, profile } = useContext(SmartLoginStateContext);
  return isThirdPartyProvider(provider) && !!(profile?.firstName || profile?.lastName);
}

function SSOSignedInBanner(): ReactElement | null {
  const { profile } = useContext(SmartLoginStateContext);
  const shouldShow = useShouldShowSSOSignedInBanner();

  if (!shouldShow) return null;

  const displayName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim();

  return (
    <div
      css={(theme) => ({
        borderRadius: '0.75rem 0.75rem 0 0',
        overflow: 'hidden',
        animation: `${fadeInAnimation} 250ms ease-out`,
        '& > [data-testid="sso-signed-in-banner"]': {
          backgroundColor: `${theme.lighten(theme.colors.info, 0.8)} !important`,
          color: `${theme.colors.info} !important`,
          fontSize: '0.875rem',
          fontWeight: 500,
          gap: '0.5rem !important',
          padding: '0.375rem 1rem !important',
        },
      })}
    >
      <ODSBanner
        testId="sso-signed-in-banner"
        color="info"
        icon="arrow-right-to-bracket"
        compact
        fullWidth
        message={
          <FormattedMessage {...messages.signedInAsViaSso} values={{ displayName }} />
        }
      />
    </div>
  );
}

export default SSOSignedInBanner;
export { useShouldShowSSOSignedInBanner };
